import { useEffect, useMemo, useRef, useState } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { api, formatFileSize } from "@/lib/api";
import { X, ChevronLeft, ChevronRight, Download as DownloadIcon, FileText, Loader2 } from "lucide-react";

const IMAGE_TYPES = new Set(["image/jpeg", "image/png", "image/webp"]);

/**
 * Anteprima a tutto schermo per una lista di allegati.
 * Permette di scorrere con ← → e di chiudere con Esc o cliccando sullo sfondo.
 *
 * Props:
 *  - items: [{ id, original_filename, content_type, size, description, kind }]
 *  - index: numero (indice in items) — il file corrente
 *  - onClose: () => void
 *  - onIndexChange: (newIndex) => void
 *  - onDownload: (att) => void (opzionale)
 *
 * Implementazione: usa Radix Dialog primitives così si compone correttamente
 * con altri Dialog Radix eventualmente già aperti (es. dialog "Documenti cliente").
 */
export function AttachmentLightbox({ items, index, onClose, onIndexChange, onDownload }) {
  const att = items[index];
  const isImage = att && IMAGE_TYPES.has(att.content_type);
  const isPdf = att && att.content_type === "application/pdf";
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const cacheRef = useRef(new Map());

  useEffect(() => {
    return () => {
      cacheRef.current.forEach((url) => URL.revokeObjectURL(url));
      cacheRef.current.clear();
    };
  }, []);

  useEffect(() => {
    if (!att) return;
    const cached = cacheRef.current.get(att.id);
    if (cached) { setBlobUrl(cached); return; }
    setLoading(true);
    setBlobUrl(null);
    api.get(`/attachments/${att.id}/download`, { responseType: "blob" })
      .then((r) => {
        const blob = new Blob([r.data], { type: att.content_type || r.data.type });
        const url = URL.createObjectURL(blob);
        cacheRef.current.set(att.id, url);
        setBlobUrl(url);
      })
      .catch(() => setBlobUrl(null))
      .finally(() => setLoading(false));
  }, [att]);

  // Tastiera ← →
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowLeft" && index > 0) onIndexChange(index - 1);
      if (e.key === "ArrowRight" && index < items.length - 1) onIndexChange(index + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, items.length, onIndexChange]);

  const meta = useMemo(() => {
    if (!att) return "";
    const parts = [];
    if (att.size) parts.push(formatFileSize(att.size));
    if (att.content_type) parts.push(att.content_type.split("/")[1].toUpperCase());
    if (att.kind) parts.push(att.kind.replace("_", " "));
    return parts.join(" · ");
  }, [att]);

  if (!att) return null;

  return (
    <DialogPrimitive.Root open={true} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          data-testid="attachment-lightbox"
          className="fixed inset-0 z-[1000] bg-black/90 flex flex-col data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        />
        <DialogPrimitive.Content
          onOpenAutoFocus={(e) => e.preventDefault()}
          className="fixed inset-0 z-[1001] flex flex-col outline-none"
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogPrimitive.Title className="sr-only">Anteprima allegato</DialogPrimitive.Title>
          {/* Toolbar superiore */}
          <div className="flex items-center justify-between gap-4 px-6 py-3 text-white">
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate" data-testid="lightbox-filename">{att.original_filename}</div>
              <div className="text-xs opacity-70 truncate">
                {index + 1} / {items.length} · {meta}
                {att.description ? <span className="ml-2 opacity-90">— {att.description}</span> : null}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {onDownload && (
                <button
                  type="button"
                  data-testid="lightbox-download"
                  onClick={() => onDownload(att)}
                  className="rounded p-2 hover:bg-white/15 text-white"
                  title="Scarica"
                >
                  <DownloadIcon className="h-5 w-5" />
                </button>
              )}
              <button
                type="button"
                data-testid="lightbox-close"
                onClick={onClose}
                className="rounded p-2 hover:bg-white/15 text-white"
                title="Chiudi (Esc)"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Viewer + frecce */}
          <div className="relative flex-1 flex items-center justify-center px-3 sm:px-12 pb-6" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <button
              type="button"
              data-testid="lightbox-prev"
              disabled={index === 0}
              onClick={(e) => { e.stopPropagation(); if (index > 0) onIndexChange(index - 1); }}
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 z-10 rounded-full p-2 bg-white/10 hover:bg-white/25 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Precedente (←)"
            >
              <ChevronLeft className="h-7 w-7" />
            </button>
            <button
              type="button"
              data-testid="lightbox-next"
              disabled={index === items.length - 1}
              onClick={(e) => { e.stopPropagation(); if (index < items.length - 1) onIndexChange(index + 1); }}
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 z-10 rounded-full p-2 bg-white/10 hover:bg-white/25 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              title="Successivo (→)"
            >
              <ChevronRight className="h-7 w-7" />
            </button>

            <div className="w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
              {loading && (
                <div className="text-white flex items-center gap-2 text-sm">
                  <Loader2 className="h-5 w-5 animate-spin" /> Caricamento…
                </div>
              )}
              {!loading && blobUrl && isImage && (
                <img
                  src={blobUrl}
                  alt={att.original_filename}
                  data-testid="lightbox-image"
                  className="max-h-full max-w-full object-contain shadow-2xl rounded"
                />
              )}
              {!loading && blobUrl && isPdf && (
                <iframe
                  src={blobUrl}
                  title={att.original_filename}
                  data-testid="lightbox-pdf"
                  className="w-full h-full bg-white rounded shadow-2xl"
                />
              )}
              {!loading && !isImage && !isPdf && (
                <div className="text-white flex flex-col items-center gap-3 text-sm bg-white/5 px-8 py-10 rounded">
                  <FileText className="h-12 w-12 opacity-70" />
                  <div>Formato non visualizzabile in anteprima.</div>
                  {onDownload && (
                    <button
                      type="button"
                      onClick={() => onDownload(att)}
                      className="mt-2 inline-flex items-center gap-2 rounded-md bg-white text-slate-900 px-4 py-2 text-sm font-medium hover:bg-white/90"
                    >
                      <DownloadIcon className="h-4 w-4" /> Scarica
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
